import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import type { FormSection, FormQuestion } from '@affogato/shared';

interface FormRendererProps {
  section: FormSection;
  answers: Record<string, unknown>;
  onAnswerChange: (questionId: string, value: unknown) => void;
  showSectionTitle?: boolean;
}

export function FormRenderer({
  section,
  answers,
  onAnswerChange,
  showSectionTitle = true,
}: FormRendererProps) {
  const { t } = useTranslation('diagnostics');

  const renderQuestion = (question: FormQuestion) => {
    const value = answers[question.id];

    switch (question.type) {
      case 'scale':
        return (
          <div className="space-y-3">
            <div className="flex justify-between text-sm text-gray-500 dark:text-slate-400">
              <span>{question.scaleConfig?.minLabel || '1 - Very Low'}</span>
              <span>{question.scaleConfig?.maxLabel || '5 - Very High'}</span>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => onAnswerChange(question.id, num)}
                  className={clsx(
                    'flex-1 py-3 rounded-xl text-sm font-medium transition-all',
                    value === num
                      ? 'bg-primary-600 text-white shadow-lg scale-105'
                      : 'bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:border-primary-500'
                  )}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        );

      case 'yes_no':
        return (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onAnswerChange(question.id, true)}
              className={clsx(
                'flex-1 py-3 rounded-xl text-sm font-medium transition-all',
                value === true
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:border-green-500'
              )}
            >
              {t('public.yes') || 'Yes'}
            </button>
            <button
              type="button"
              onClick={() => onAnswerChange(question.id, false)}
              className={clsx(
                'flex-1 py-3 rounded-xl text-sm font-medium transition-all',
                value === false
                  ? 'bg-red-600 text-white shadow-lg'
                  : 'bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:border-red-500'
              )}
            >
              {t('public.no') || 'No'}
            </button>
          </div>
        );

      case 'text':
        return (
          <textarea
            value={(value as string) || ''}
            onChange={(e) => onAnswerChange(question.id, e.target.value)}
            rows={4}
            placeholder={t('builder.questionTypes.text')}
            className="w-full px-4 py-3 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none placeholder-gray-400 dark:placeholder-slate-500"
          />
        );

      case 'single_choice':
        return (
          <div className="space-y-2">
            {question.choices?.map((choice) => (
              <button
                key={choice.id}
                type="button"
                onClick={() => onAnswerChange(question.id, choice.id)}
                className={clsx(
                  'w-full flex items-center gap-3 p-4 rounded-xl transition-all text-left',
                  value === choice.id
                    ? 'bg-primary-50 dark:bg-primary-900/30 border-2 border-primary-500'
                    : 'bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 hover:border-primary-300'
                )}
              >
                <div
                  className={clsx(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                    value === choice.id
                      ? 'border-primary-500'
                      : 'border-gray-300 dark:border-slate-500'
                  )}
                >
                  {value === choice.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />
                  )}
                </div>
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  {choice.text}
                </span>
              </button>
            ))}
          </div>
        );

      case 'multiple_choice':
        const selectedValues = (value as string[]) || [];
        return (
          <div className="space-y-2">
            {question.choices?.map((choice) => {
              const isSelected = selectedValues.includes(choice.id);
              return (
                <button
                  key={choice.id}
                  type="button"
                  onClick={() => {
                    const newValues = isSelected
                      ? selectedValues.filter((v) => v !== choice.id)
                      : [...selectedValues, choice.id];
                    onAnswerChange(question.id, newValues);
                  }}
                  className={clsx(
                    'w-full flex items-center gap-3 p-4 rounded-xl transition-all text-left',
                    isSelected
                      ? 'bg-primary-50 dark:bg-primary-900/30 border-2 border-primary-500'
                      : 'bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 hover:border-primary-300'
                  )}
                >
                  <div
                    className={clsx(
                      'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0',
                      isSelected
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-300 dark:border-slate-500'
                    )}
                  >
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                        <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-gray-700 dark:text-slate-300">
                    {choice.text}
                  </span>
                </button>
              );
            })}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {/* Section Header */}
      {showSectionTitle && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {section.title}
          </h2>
          {section.description && (
            <p className="text-gray-500 dark:text-slate-400 mt-2">
              {section.description}
            </p>
          )}
        </div>
      )}

      {/* Questions */}
      {section.questions.map((question, idx) => (
        <div
          key={question.id}
          className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-6 shadow-sm"
        >
          <div className="mb-4">
            <div className="flex items-start gap-3">
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-2 py-0.5 rounded">
                {idx + 1}
              </span>
              <div className="flex-1">
                <p className="text-base font-medium text-gray-900 dark:text-white">
                  {question.text}
                  {question.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </p>
                {question.description && (
                  <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                    {question.description}
                  </p>
                )}
              </div>
            </div>
          </div>
          {renderQuestion(question)}
        </div>
      ))}
    </div>
  );
}
